import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import { DownloadIcon, BackIcon, UploadIcon } from "../../../components/Dashboard/DataCenterIcons";
import "../../../assets/styles/RegularVDCPage.css";
import "../../../assets/styles/FDCAppendReuploadPage.css"; // reuse styles
import { ROUTES, headers } from "../../../utils/constants";
import commonService from "../../../services/commonService";

const RegularVDCPage: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [data, setData] = useState<any>(null);

  /* ---------- File handlers ---------- */

  const handleFile = (f: File) => {
    if (!f.name.toLowerCase().endsWith(".csv")) {
      alert("Only CSV files allowed");
      return;
    }
    setFile(f);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ---------- Upload ---------- */

  const onUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file); // IMPORTANT

      const response = await commonService.vdcUpload(formData);
      setData(response.data);

      setTimeout(() => setData(null), 10000);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsLoading(false);
      clearFile();
    }
  };

  /* ---------- Download template ---------- */

  const download = async () => {
    const blob = new Blob([headers.join(",")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);

    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");

    link.download = `vdc_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${now.getFullYear()}.csv`;
    link.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <MainLayout>
      <div className="fdc-append-reupload-page">
        {/* Header */}
        <div className="regular-vdc-header">
          <div className="header-left">
            <button onClick={() => navigate(-1)} className="regular-rdc-back-button">
              <BackIcon /> Back
            </button>
            {/* <span className="regular-vdc-subtitle">Verified data upload</span> */}
          </div>

          <button onClick={download} className="rde-download-template-button">
            <DownloadIcon /> Download template
          </button>
        </div>

        {/* Upload Card */}
        <div className="upload-container">
          <div className="upload-card">
            <h4 title="Verified data upload" className="mb-4">Upload CSV File (VDC)</h4>

            <div
              className={`file-input-area ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                hidden
                onChange={onFileChange}
                disabled={isLoading}
              />

              <div className="upload-placeholder">
                <UploadIcon />
                <p>{file ? file.name : "Click to browse or drag and drop CSV"}</p>
                <p className="upload-hint">
                  Supports CSV files • Max 10MB
                </p>
              </div>
            </div>

            {/* Selected file */}
            {file && (
              <div className="selected-file-card">
                <div className="file-info">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size" style={{ textAlign: 'left' }}>
                    {formatFileSize(file.size)}
                  </span>
                </div>
                <button className="remove-file-btn" onClick={clearFile} disabled={isLoading}>×</button>
              </div>
            )}

            {/* Actions */}
            <div className="action-buttons">
              <button
                className="btn-secondary"
                onClick={clearFile}
                disabled={!file || isLoading}
              >
                Clear
              </button>

              <button
                className="btn-primary"
                onClick={onUpload}
                disabled={!file || isLoading}
              >
                {isLoading ? "Uploading..." : "Upload"}
              </button>
            </div>

            {/* Success */}
            {data?.id && (
              <div className="success-message mt-3">
                ✅ CSV uploaded successfully. Tracking ID: <strong>VDC_{data.id}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="regular-vdc-status">
          <button onClick={() => navigate(ROUTES.VDC_LIST)} className="status-button">
            Status of uploaded data
          </button>
        </div>
      </div>
    </MainLayout>
  );
};

export default RegularVDCPage;
